/**
 * <Money /> — single source of truth for currency display.
 *
 * Why this exists: the entire app previously hardcoded `GH₵${x.toFixed(2)}`,
 * which meant flipping the CurrencySwitcher in the Header did absolutely
 * nothing outside the group view. <Money /> reads from DisplayCurrencyContext
 * so a single switcher change re-renders every money value app-wide.
 *
 * Two input modes:
 *   - <Money minor={amountInPesewas} />     ← preferred. Integer-safe.
 *   - <Money major={amountInCedis} />       ← compat with existing float-cedis state.
 *
 * Pick exactly one. If both are provided, `minor` wins.
 *
 * Compact mode:
 *   <Money minor={...} compact />           ← 1.2M, 450K, etc. when ≥ 1M.
 *
 * Negative-styling, em-dash placeholder for null/undefined, all built in.
 *
 * Example migration:
 *   - {`GH₵${total.toFixed(2)}`}
 *   + <Money major={total} />
 *
 *   - {`GH₵${(totalMinor / 100).toFixed(2)}`}
 *   + <Money minor={totalMinor} />
 */
import React from "react";
import { useDisplayCurrency } from "@/context/DisplayCurrencyContext";

interface MoneyProps {
  /** Amount in MINOR units (pesewas). Preferred — integer-safe. */
  minor?: number | null | undefined;
  /** Amount in MAJOR units (cedis float). Use when minor isn't available. */
  major?: number | null | undefined;
  /** Show compact notation (1.2M) when ≥ 1,000,000 */
  compact?: boolean;
  /** Apply negative tint when value < 0 */
  signed?: boolean;
  /** Override placeholder for null/undefined */
  placeholder?: string;
  /** Extra className passed through to the span */
  className?: string;
  /** title attribute (tooltip) */
  title?: string;
}

const Money: React.FC<MoneyProps> = ({
  minor,
  major,
  compact = false,
  signed = false,
  placeholder = "—",
  className = "",
  title,
}) => {
  const { format } = useDisplayCurrency();

  // Resolve to a single minor-units value
  let minorValue: number | null = null;
  if (minor !== undefined && minor !== null) {
    minorValue = minor;
  } else if (major !== undefined && major !== null) {
    minorValue = Math.round(major * 100);
  }

  if (minorValue === null || Number.isNaN(minorValue)) {
    return (
      <span className={`tabular-nums ${className}`} title={title}>
        {placeholder}
      </span>
    );
  }

  const formatted = format(minorValue, { compact });
  const negativeClass =
    signed && minorValue < 0 ? " text-rose-600 dark:text-rose-400" : "";

  return (
    <span
      className={`tabular-nums${negativeClass} ${className}`.trim()}
      title={title}
    >
      {formatted}
    </span>
  );
};

export default Money;

// ─── Convenience hook for cases where you need just the formatter ────
// Useful when the value is interpolated inside a longer string, or
// passed to a chart library that wants a string, not a React node.
//
// Example:
//   const fmt = useMoneyFormat();
//   const tooltip = `Revenue last month: ${fmt.minor(123456)}`;
//   <Chart formatter={(v) => fmt.major(v)} />
export function useMoneyFormat() {
  const { format } = useDisplayCurrency();
  return {
    /** Format minor units (pesewas) */
    minor: (value: number | null | undefined, opts?: { compact?: boolean }) => {
      if (value === null || value === undefined || Number.isNaN(value))
        return "—";
      return format(value, opts);
    },
    /** Short label for chart axes: 45K, 1.2M. */
    axis: (value: number | null | undefined) => {
      if (value === null || value === undefined || Number.isNaN(value)) return "";
      return format(value, { short: true });
    },
    /** Format major units (cedis float) */
    major: (value: number | null | undefined, opts?: { compact?: boolean }) => {
      if (value === null || value === undefined || Number.isNaN(value))
        return "—";
      return format(Math.round(value * 100), opts);
    },
  };
}

/**
 * Screens (dashboards, lists, reports): format minor units in the currency
 * the person is VIEWING in — the header switcher converts every amount.
 * null / undefined → 0. Receipts, statements, payslips, tills and anything
 * typed in stay in the business currency: use formatMoney there.
 *
 *   const money = useMoney();
 *   <td>{money(row.totalMinor)}</td>
 */
export function useMoney(): (minor: number | null | undefined) => string {
  const { format } = useDisplayCurrency();
  return React.useCallback((minor: number | null | undefined) => format(Number(minor) || 0), [format]);
}

/** For amounts already in major units (12.5). A missing value shows "—" (e.g. a product with no cost yet). */
export function useMoneyMajor(): (major: number | null | undefined) => string {
  const { format } = useDisplayCurrency();
  return React.useCallback(
    (major: number | null | undefined) => (major === null || major === undefined || Number.isNaN(Number(major)) ? "—" : format(Math.round(Number(major) * 100))),
    [format],
  );
}
